import { capitalize } from "lodash"

import { DateFormatType, formatDate } from "~/utils/datetime"
import { getFilterOperators, type Filter, type FilterFieldType } from "~/utils/filter"
import { formatNumber } from "~/utils/formatting"

type FilterLabelParts = {
  /** Field display name, original casing (e.g. "Title & summary"). */
  field: string
  /** Operator display name, original casing (e.g. "Is one of"). */
  operator: string
  /** Formatted value, or null for valueless operators (e.g. "Is empty"). */
  value: string | null
}

/**
 * Break a filter into its display parts (field / operator / value). Shared by the
 * single-line popover trigger text and the segmented read-only pills in the view-details
 * hover card. Returns null when the field or operator can't be resolved.
 */
function getFilterLabelParts(
  filter: Filter,
  allFilterFields: FilterFieldType[],
  getUserName: (id: string) => string,
): FilterLabelParts | null {
  const field = allFilterFields.find(
    (f) =>
      f.value === filter.field ||
      (f.value === "DynamicFilterField" && f.dynamicValue === filter.field),
  )
  const operator = getFilterOperators(filter.field, allFilterFields).find(
    (op) => op.value === filter.operator,
  )

  if (!field || !operator) {
    return null
  }

  if (operator.valueless) {
    return { field: field.name, operator: operator.name, value: null }
  }

  let value: string
  if (operator.arrayValue) {
    if (filter.values.length > 4) {
      value = `${filter.values.slice(0, 2).join(", ")} and ${filter.values.length - 2} more`
    } else {
      value =
        filter.values.length > 1
          ? `${filter.values.slice(0, -1).join(", ")} or ${filter.values[filter.values.length - 1]}`
          : filter.values[0] || ""
    }
  } else {
    value = filter.value
    if (field.type === "user") {
      value = getUserName(filter.value)
    } else if (field.type === "date") {
      value = formatDate(filter.value, DateFormatType.FullDate)
    } else if (field.type === "number") {
      value = Number.isNaN(Number(filter.value)) ? filter.value : formatNumber(Number(filter.value))
    }
  }

  return { field: field.name, operator: operator.name, value }
}

/**
 * Human-readable label for a single filter, e.g. "Brand is one of Nike or Adidas".
 * Used by the filter popover trigger text.
 */
export function formatFilterLabel(
  filter: Filter,
  allFilterFields: FilterFieldType[],
  getUserName: (id: string) => string,
): string {
  const parts = getFilterLabelParts(filter, allFilterFields, getUserName)
  if (!parts) {
    return "Unknown filter"
  }
  const fieldName = capitalize(parts.field.toLowerCase())
  const operatorName = parts.operator.toLowerCase()
  return parts.value != null
    ? `${fieldName} ${operatorName} ${parts.value}`
    : `${fieldName} ${operatorName}`
}
